import { useEffect, useState } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { Layout as AntLayout, Menu, Button, Dropdown, Drawer, Grid, Tag } from 'antd'
import {
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  FormOutlined,
  TeamOutlined,
  SettingOutlined,
  DashboardOutlined,
  SwapOutlined,
  UsergroupAddOutlined,
  LockOutlined,
  ApartmentOutlined,
} from '@ant-design/icons'
import { useAuthStore, usePodeTrocarParceiro } from '@/stores/authStore'
import { useRotuloDeFilho } from '@/lib/rotuloDeFilho'
import { useContextoDoJivo } from '@/lib/contextoDoJivo'
import { useConfiguracaoStore } from '@/stores/configuracaoStore'
import { ModalDeVideoDeBoasVindas } from '@/components/ModalDeVideoDeBoasVindas'
import { AvisoDeSenhaPadrao } from '@/components/AvisoDeSenhaPadrao'
import newDriverLogo from '@/assets/logo.svg'

const { Header, Sider, Content } = AntLayout
const { useBreakpoint } = Grid

// ─── Entry component ─────────────────────────────────────────────────────────

export function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const screens = useBreakpoint()
  const isMobile = !screens.md

  useCarregarConfiguracaoDoAmbiente()
  useContextoDoJivo()

  return (
    <AntLayout className="min-h-screen">
      {isMobile ? (
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      ) : (
        <DesktopSidebar />
      )}

      <AntLayout>
        <ModalDeVideoDeBoasVindas />
        <AvisoDeSenhaPadrao />
        <AppHeader showMenuButton={isMobile} onMenuOpen={() => setDrawerOpen(true)} />
        <Content className="p-4 md:p-6 bg-[#f3f4f5]">
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  )
}

/**
 * Mesmo miolo, sem a moldura: é o que o Hyperpay embute num iframe, onde a navegação é a do
 * Hyperpay. Sem menu e sem cabeçalho não há para onde ir — as rotas embutidas param na matrícula
 * por não existirem, não por alguma tela se esconder.
 */
export function LayoutEmbutido() {
  useCarregarConfiguracaoDoAmbiente()
  useContextoDoJivo()

  return (
    <div className="min-h-screen bg-[#f3f4f5]">
      <ModalDeVideoDeBoasVindas />
      <div className="p-4 md:p-6">
        <Outlet />
      </div>
    </div>
  )
}

/**
 * Embutido, navegar para fora da matrícula abre o painel inteiro dentro do iframe do Hyperpay —
 * com moldura, menu e dois cabeçalhos empilhados. Quem oferece um link para outra tela pergunta
 * aqui antes.
 */
export function useEstaEmbutido(): boolean {
  return useLocation().pathname.startsWith('/embutido')
}

// ─── Helper hooks ─────────────────────────────────────────────────────────────

/**
 * O banner e o menu dependem de saber em que AMBIENTE estamos, e essa resposta vem junto da
 * configuração do parceiro. Antes só a tela de Configurações a buscava, então o banner não
 * apareceria em nenhuma outra. Buscar aqui vale para todas as páginas do painel, e o Layout só
 * renderiza dentro de AuthGuard + ContractGuard, ou seja, sempre com um parceiro selecionado.
 */
function useCarregarConfiguracaoDoAmbiente() {
  const carregado = useConfiguracaoStore((s) => s.carregado)
  const fetchConfig = useConfiguracaoStore((s) => s.fetchConfig)

  useEffect(() => {
    if (!carregado) void fetchConfig()
  }, [carregado, fetchConfig])
}

// ─── Helper components ────────────────────────────────────────────────────────

function DesktopSidebar() {
  return (
    <Sider width={220} theme="light" className="border-r border-gray-200 !bg-white">
      <SidebarLogo />
      <SidebarNav />
    </Sider>
  )
}

function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      styles={{ wrapper: { width: 220 }, body: { padding: 0 } }}
      title={null}
      closable={false}
    >
      <SidebarLogo />
      <SidebarNav onNavigate={onClose} />
    </Drawer>
  )
}

function AppHeader({
  showMenuButton,
  onMenuOpen,
}: {
  showMenuButton: boolean
  onMenuOpen: () => void
}) {
  return (
    <Header className="!bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6 !h-14">
      {showMenuButton ? (
        <Button icon={<MenuOutlined />} type="text" onClick={onMenuOpen} />
      ) : (
        <div />
      )}
      <UserMenu />
    </Header>
  )
}

function SidebarLogo() {
  return (
    <div className="h-14 flex items-center justify-center border-b border-gray-200 px-4">
      <NavLink to="/matricula" className="text-lg font-bold text-[#1a3a5c] no-underline">
        <img src={newDriverLogo} />
      </NavLink>
    </div>
  )
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation()
  const podeVerPrecos = useAuthStore(
    (s) => s.parceiro?.role === 'admin' && s.parceiro?.parceiroPaiId == null
  )
  const rotuloDeFilho = useRotuloDeFilho()
  const selectedKey = location.pathname.startsWith('/dashboard')
    ? 'dashboard'
    : location.pathname.startsWith('/alunos') || location.pathname.startsWith('/detalhes')
      ? 'alunos'
      : location.pathname.startsWith('/turmas')
        ? 'turmas'
        : location.pathname.startsWith('/precos')
          ? 'precos'
          : location.pathname.startsWith('/configuracoes')
            ? 'configuracoes'
            : 'matricula'

  return (
    <Menu
      mode="inline"
      className="!border-none mt-2"
      selectedKeys={[selectedKey]}
      items={[
        {
          key: 'dashboard',
          icon: <DashboardOutlined />,
          label: (
            <NavLink to="/dashboard" onClick={onNavigate}>
              Painel
            </NavLink>
          ),
        },
        {
          key: 'matricula',
          icon: <FormOutlined />,
          label: (
            <NavLink to="/matricula" onClick={onNavigate}>
              Matricular
            </NavLink>
          ),
        },
        {
          key: 'alunos',
          icon: <TeamOutlined />,
          label: (
            <NavLink to="/alunos" onClick={onNavigate}>
              Alunos
            </NavLink>
          ),
        },
        {
          key: 'turmas',
          icon: <ApartmentOutlined />,
          label: (
            <NavLink to="/turmas" onClick={onNavigate}>
              Turmas
            </NavLink>
          ),
        },
        ...(podeVerPrecos
          ? [
              {
                key: 'precos',
                icon: <UsergroupAddOutlined />,
                label: (
                  <NavLink to="/precos" onClick={onNavigate}>
                    {rotuloDeFilho.plural}
                  </NavLink>
                ),
              },
            ]
          : []),
        {
          key: 'configuracoes',
          icon: <SettingOutlined />,
          label: (
            <NavLink to="/configuracoes" onClick={onNavigate}>
              Configurações
            </NavLink>
          ),
        },
      ]}
    />
  )
}

function UserMenu() {
  const usuario = useAuthStore((s) => s.usuario)
  const parceiro = useAuthStore((s) => s.parceiro)
  const logout = useAuthStore((s) => s.logout)
  // Vale para o administrador da New Driver e para quem tem mais de um vínculo (spec 017).
  const podeTrocarParceiro = usePodeTrocarParceiro()
  const navigate = useNavigate()

  async function handleLogout() {
    await logout()
    navigate('/login')
  }

  const menuItems = [
    ...(podeTrocarParceiro
      ? [
          {
            key: 'trocar',
            icon: <SwapOutlined />,
            label: 'Trocar Parceiro',
            onClick: () => navigate('/selecionar-parceiro'),
          },
        ]
      : []),
    {
      key: 'senha',
      icon: <LockOutlined />,
      label: 'Editar senha',
      onClick: () => navigate('/alterar-senha'),
    },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Sair', onClick: handleLogout },
  ]

  return (
    <div className="flex items-center gap-3">
      {podeTrocarParceiro && parceiro && (
        <Tag color="blue" className="hidden sm:inline-flex items-center">
          {parceiro.nome}
        </Tag>
      )}
      <Dropdown menu={{ items: menuItems }} placement="bottomRight">
        <Button type="text" icon={<UserOutlined />} className="flex items-center gap-2">
          <span className="hidden sm:inline">{usuario?.nome}</span>
        </Button>
      </Dropdown>
    </div>
  )
}
